import React, { useRef, useEffect, useContext, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import produce from 'immer';
import ReactDOM from 'react-dom';
import { difference, _cs, randomString } from '@togglecorp/fujs';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import ResizeObserver from 'resize-observer-polyfill';

function getLayerName(sourceKey, layerKey) {
  return `${sourceKey}›${layerKey}`;
}
function usePrevious(value, initialValue) {
  const ref = useRef(initialValue);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}
function setMapboxToken(token) {
  mapboxgl.accessToken = token;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop$8 = () => {};

const initialMapChildState = {
  map: undefined,
  mapContainerRef: undefined,
  mapStyle: 'mapbox://styles/mapbox/streets-v11',
  setSource: noop$8,
  getSource: () => undefined,
  removeSource: noop$8,
  isSourceDefined: () => false,
  isMapDestroyed: () => false,
  setBounds: noop$8,
  debug: false
};
const MapChildContext = /*#__PURE__*/React.createContext(initialMapChildState);
const initialSourceChildState = {
  map: undefined,
  mapStyle: 'mapbox://styles/mapbox/streets-v11',
  sourceKey: undefined,
  isSourceDefined: () => false,
  isMapDestroyed: () => false,
  setLayer: noop$8,
  getLayer: () => undefined,
  removeLayer: noop$8,
  debug: false
};
const SourceChildContext = /*#__PURE__*/React.createContext(initialSourceChildState);

const noop$7 = () => {}; // TODO: add mapStyle if necessary


const MapTooltip = props => {
  const {
    map
  } = useContext(MapChildContext);
  const {
    children,
    coordinates,
    hidden,
    tooltipOptions,
    onHide,
    trackPointer
  } = props; // const popupUpdateTimeoutRef = useRef<number | undefined>();

  const tooltipContainerRef = useRef(null);
  const popupRef = useRef(null);
  const [initialTooltipOptions] = useState(tooltipOptions);
  const [initialTrackPointer] = useState(trackPointer);
  const [initialCoordinates] = useState(coordinates); // Create tooltip <div>

  useEffect(() => {
    tooltipContainerRef.current = document.createElement('div');
    return () => {
      if (tooltipContainerRef.current) {
        tooltipContainerRef.current.remove();
      }
    };
  }, []); // Render react component in tooltip <div>

  useEffect(() => {
    if (!map) {
      return;
    }

    ReactDOM.render(children, tooltipContainerRef.current);
  }, [map, children]); // Create mapbox popup and assign to tooltip <div>

  useEffect(() => {
    if (!map || !tooltipContainerRef.current || hidden) {
      return noop$7;
    }

    popupRef.current = new mapboxgl.Popup(initialTooltipOptions);

    if (initialCoordinates) {
      popupRef.current.setLngLat(initialCoordinates);
    }

    if (initialTrackPointer) {
      popupRef.current.trackPointer();
    }

    popupRef.current.setDOMContent(tooltipContainerRef.current);
    popupRef.current.addTo(map);
    /*
    // NOTE: this is a fix for bad initial popup placement
    popupUpdateTimeoutRef.current = window.setTimeout(
        () => {
            if (popupRef.current) {
                // eslint-disable-next-line no-underscore-dangle
                popupRef.current._update();
            }
        },
        0,
    );
    */

    return () => {
      /*
      if (popupUpdateTimeoutRef.current) {
          window.clearTimeout(popupUpdateTimeoutRef.current);
      }
      */
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    };
  }, [map, hidden, initialTooltipOptions, initialTrackPointer, initialCoordinates]); // Handle coordinates change

  useEffect(() => {
    if (!map || !popupRef.current || !coordinates || initialTrackPointer) {
      return;
    }

    popupRef.current.setLngLat(coordinates);
  }, [map, coordinates, initialTrackPointer]); // Handle onHide change

  useEffect(() => {
    if (!map || !popupRef.current) {
      return noop$7;
    }

    popupRef.current.on('close', () => {
      if (onHide) {
        onHide();
      }
    });
    return () => {
      if (popupRef.current) {
        popupRef.current.off('close');
      }
    };
  }, [map, onHide]);
  return null;
};

MapTooltip.defaultProps = {
  hidden: false,
  trackPointer: false
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop$6 = () => {};

function useCounter(initialValue = 0) {
  const [value, updateValue] = useState(initialValue);
  const increaseValue = useCallback(() => {
    updateValue(v => v + 1);
  }, []);
  return [increaseValue, value];
}

const MapSource = props => {
  const {
    sourceOptions,
    sourceKey,
    children,
    geoJson,
    createMarkerElement
  } = props;
  const {
    map,
    mapStyle,
    setSource,
    getSource,
    removeSource,
    isSourceDefined,
    isMapDestroyed,
    debug
  } = useContext(MapChildContext);
  const [initialDebug] = useState(debug);
  const [forceUpdate] = useCounter(0);
  const [initialGeoJson] = useState(geoJson);
  const [initialSourceOptions] = useState(sourceOptions); // Add source in mapboxgl and notify addition to parent

  useEffect(() => {
    if (!map || !sourceKey || !mapStyle) {
      return noop$6;
    }

    const options = initialSourceOptions.type === 'geojson' ? { ...initialSourceOptions,
      data: initialGeoJson
    } : initialSourceOptions;

    if (initialDebug) {
      console.warn(`Creating new source: ${sourceKey}`, options);
    }

    try {
      map.addSource(sourceKey, options);
    } catch (e) {
      console.error(e);
    }

    const destroy = () => {
      const source = getSource(sourceKey);

      if (!source) {
        // console.error(`No source named: ${sourceKey}`);
        return;
      }

      Object.entries(source.layers).forEach(([, layer]) => {
        layer.destroy();
      });
      removeSource(sourceKey);
    };

    setSource({
      name: sourceKey,
      destroy,
      layers: {}
    });
    forceUpdate();
    return destroy;
  }, [map, mapStyle, sourceKey, forceUpdate, getSource, removeSource, setSource, initialGeoJson, initialSourceOptions, initialDebug]); // Handle geoJson change
  // TODO: don't call in first render

  useEffect(() => {
    if (!map || !sourceKey || !geoJson || !mapStyle) {
      return;
    }

    const source = map.getSource(sourceKey);

    if (source.type === 'geojson') {
      if (initialDebug) {
        console.warn(`Setting source geojson: ${sourceKey}`);
      }

      source.setData(geoJson);
    }
  }, [map, mapStyle, sourceKey, geoJson, initialGeoJson, initialDebug]);
  const markers = useRef({});
  const markersOnScreen = useRef({});
  const updateMarkers = useCallback(() => {
    if (!map || !createMarkerElement || !sourceKey) {
      return;
    }

    const newMarkers = {};
    const features = map.querySourceFeatures(sourceKey);
    features.forEach(feature => {
      if (feature.geometry.type !== 'Point') {
        return;
      }

      const {
        geometry: {
          coordinates
        },
        properties
      } = feature;

      if (!properties || !properties.cluster) {
        return;
      }

      const {
        cluster_id: clusterId
      } = properties;
      let marker = markers.current[clusterId];

      if (!marker) {
        const el = createMarkerElement(properties);
        marker = new mapboxgl.Marker({
          element: el
        }).setLngLat(coordinates);
        markers.current[clusterId] = marker;
      }

      newMarkers[clusterId] = marker;

      if (!markersOnScreen.current[clusterId]) {
        marker.addTo(map);
      }
    });
    Object.keys(markersOnScreen.current).forEach(markerId => {
      if (!newMarkers[markerId]) {
        markersOnScreen.current[markerId].remove();
      }
    });
    markersOnScreen.current = newMarkers;
  }, [map, createMarkerElement, sourceKey]);
  useEffect(() => {
    if (!map || !sourceKey || !mapStyle || !geoJson || !createMarkerElement) {
      return noop$6;
    }
    /*
    const handleData = (e: mapboxgl.EventData) => {
        if (e.sourceId !== sourceKey || !e.isSourceLoaded) {
            return;
        }
        updateMarkers();
    };
    map.on('data', handleData);
    */


    map.on('move', updateMarkers);
    map.on('moveend', updateMarkers);
    return () => {
      // map.off('data', handleData);
      map.off('move', updateMarkers);
      map.off('moveend', updateMarkers);
    };
  }, [map, sourceKey, mapStyle, createMarkerElement, updateMarkers, geoJson]);
  useEffect(() => {
    if (!map || !sourceKey || !mapStyle || !geoJson || !createMarkerElement) {
      return noop$6;
    }

    const interval = setInterval(updateMarkers, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [map, sourceKey, mapStyle, createMarkerElement, updateMarkers, geoJson]);
  const getLayer = useCallback(layerKey => {
    const source = getSource(sourceKey);

    if (!source) {
      return undefined;
    }

    return source.layers[layerKey];
  }, [sourceKey, getSource]);
  const setLayer = useCallback((name, method) => {
    // const { name } = layer;
    const source = getSource(sourceKey);

    if (!source) {
      console.error(`No source named: ${sourceKey}`);
      return;
    } // console.warn(`Registering layer: ${name}`);


    const newSource = produce(source, safeSource => {
      const value = method(source.layers[name]);

      if (value !== undefined) {
        // eslint-disable-next-line no-param-reassign
        safeSource.layers[name] = value;
      } else {
        // eslint-disable-next-line no-param-reassign
        delete safeSource.layers[name];
      }
    });
    setSource(newSource);
  }, [sourceKey, getSource, setSource]);
  const removeLayer = useCallback(layerKey => {
    const source = getSource(sourceKey);

    if (!source) {
      console.error(`No source named: ${sourceKey}`);
      return;
    }

    const layer = source.layers[layerKey];

    if (!layer) {
      console.error(`No layer named: ${layerKey}`, source);
      return;
    } // NOTE: check if map is dis-mounted?


    if (map) {
      const id = getLayerName(sourceKey, layerKey);

      if (initialDebug) {
        console.warn(`Removing layer: ${id}`);
      }

      map.removeLayer(id);
    } // console.warn(`Registering layer: ${layerKey}`);


    const newSource = produce(source, safeSource => {
      // eslint-disable-next-line no-param-reassign
      delete safeSource.layers[layerKey];
    });
    setSource(newSource);
  }, [map, sourceKey, getSource, setSource, initialDebug]);

  if (!isSourceDefined(sourceKey)) {
    return null;
  }

  const childrenProps = {
    map,
    mapStyle,
    sourceKey,
    getLayer,
    setLayer,
    removeLayer,
    isSourceDefined,
    isMapDestroyed,
    debug: initialDebug
  };
  return /*#__PURE__*/React.createElement(SourceChildContext.Provider, {
    value: childrenProps
  }, children);
};

const noop$5 = () => {};

function MapState(props) {
  const {
    sourceLayer,
    attributeKey,
    attributes
  } = props;
  const {
    map,
    mapStyle,
    sourceKey,
    isSourceDefined,
    isMapDestroyed,
    debug
  } = useContext(SourceChildContext);
  const [initialAttributes] = useState(attributes);
  const [initialDebug] = useState(debug);
  const prevAttributes = usePrevious(attributes, []);
  const prevSourceLayer = usePrevious(sourceLayer, undefined); // Handle attributes change

  useEffect(() => {
    if (!map || !sourceKey) {
      return noop$5;
    }

    if (initialDebug) {
      // eslint-disable-next-line max-len
      console.warn(`Setting source state: "${sourceKey}" with "${sourceLayer || 'no'}" source layer.`);
    }

    initialAttributes.forEach(attribute => {
      map.setFeatureState({
        id: attribute.id,
        source: sourceKey,
        sourceLayer
      }, {
        [attributeKey]: attribute.value
      });
    });
    return () => {
      if (!isMapDestroyed() && isSourceDefined(sourceKey)) {
        if (initialDebug) {
          console.warn(`Removing source state: "${sourceKey}" with "${sourceLayer || 'no'}" source layer.`);
        }

        initialAttributes.forEach(attribute => {
          map.removeFeatureState({
            id: attribute.id,
            source: sourceKey,
            sourceLayer
          }, attributeKey);
        });
      }
    };
  }, [map, mapStyle, sourceKey, sourceLayer, attributeKey, initialAttributes, isSourceDefined, isMapDestroyed, initialDebug]);
  useEffect(() => {
    if (!map || !sourceKey) {
      return;
    }

    if (sourceLayer !== prevSourceLayer) {
      prevAttributes.forEach(attribute => {
        map.removeFeatureState({
          id: attribute.id,
          source: sourceKey,
          sourceLayer: prevSourceLayer
        }, attributeKey);
      });
      attributes.forEach(attribute => {
        map.setFeatureState({
          id: attribute.id,
          source: sourceKey,
          sourceLayer
        }, {
          [attributeKey]: attribute.value
        });
      });
      return;
    }

    const toBeDeleted = difference(new Set(prevAttributes), new Set(attributes));
    const toBeAddedModified = difference(new Set(attributes), toBeDeleted);
    toBeDeleted.forEach(attribute => {
      map.removeFeatureState({
        id: attribute.id,
        source: sourceKey,
        sourceLayer
      }, attributeKey);
    });
    toBeAddedModified.forEach(attribute => {
      map.setFeatureState({
        id: attribute.id,
        source: sourceKey,
        sourceLayer
      }, {
        [attributeKey]: attribute.value
      });
    });
  }, [map, sourceKey, attributes, prevAttributes, attributeKey, sourceLayer, prevSourceLayer]);
  return null;
}

MapState.defaultProps = {
  attributes: []
};

// import { Layer } from '../type';
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop$4 = () => {};

// eslint-disable-next-line @typescript-eslint/ban-types
function removeUndefined(obj) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cleanNewLayerOptions = {};
  Object.keys(obj).forEach(key => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (key && obj[key]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      cleanNewLayerOptions[key] = obj[key];
    }
  });
  return cleanNewLayerOptions;
}

const MapLayer = props => {
  const {
    layerKey,
    layerOptions,
    onClick,
    onDoubleClick,
    onMouseEnter,
    onDrag,
    onDragEnd,
    onMouseLeave,
    beneath,
    onAnimationFrame
  } = props;
  const {
    map,
    mapStyle,
    sourceKey,
    setLayer,
    removeLayer,
    getLayer,
    debug
  } = useContext(SourceChildContext);
  const [initialLayerOptions] = useState(layerOptions);
  const [initialBeneath] = useState(beneath);
  const [initialDebug] = useState(debug);
  const animationKeyRef = useRef(); // Add layer in mapboxgl

  useEffect(() => {
    if (!map || !sourceKey || !layerKey) {
      return noop$4;
    }

    const id = getLayerName(sourceKey, layerKey);

    if (initialDebug) {
      console.warn(`Creating new layer: ${id}`);
    }

    const newLayerOptions = removeUndefined({ ...initialLayerOptions,
      id,
      source: sourceKey
    });
    map.addLayer(newLayerOptions, initialBeneath);

    const destroy = () => {
      const layer = getLayer(layerKey);

      if (!layer) {
        // console.error(`No layer named: ${id}`);
        return;
      }

      removeLayer(layerKey);
    };

    setLayer(layerKey, () => ({
      name: layerKey,
      destroy
    }));
    return destroy;
  }, [map, mapStyle, sourceKey, layerKey, initialLayerOptions, initialBeneath, initialDebug, getLayer, removeLayer, setLayer]); // Notify modification on layer

  useEffect(() => {
    if (!map || !sourceKey || !layerKey) {
      return;
    }

    setLayer(layerKey, layer => layer && { ...layer,
      onClick,
      onDoubleClick,
      onMouseEnter,
      onMouseLeave,
      onDrag,
      onDragEnd
    });
  }, [map, sourceKey, layerKey, onClick, onDoubleClick, onMouseEnter, onMouseLeave, onDrag, onDragEnd, setLayer]);
  const {
    paint,
    filter,
    layout
  } = layerOptions; // Handle paint change
  // TODO: don't call in first render

  useEffect(() => {
    if (!map || !sourceKey || !layerKey || !paint) {
      return;
    }

    const id = getLayerName(sourceKey, layerKey);
    Object.entries(paint).forEach(([key, value]) => {
      map.setPaintProperty(id, key, value);
    });
  }, [map, sourceKey, layerKey, paint]); // Handle layout change
  // TODO: don't call in first render

  useEffect(() => {
    if (!map || !sourceKey || !layerKey || !layout) {
      return;
    }

    const id = getLayerName(sourceKey, layerKey);
    Object.entries(layout).forEach(([key, value]) => {
      map.setLayoutProperty(id, key, value);
    });
  }, [map, sourceKey, layerKey, layout]); // Handle filter change
  // TODO: don't call in first render

  useEffect(() => {
    if (!map || !sourceKey || !layerKey) {
      return;
    }

    const id = getLayerName(sourceKey, layerKey);
    map.setFilter(id, filter);
  }, [map, sourceKey, layerKey, filter]);
  useEffect(() => {
    if (!map || !sourceKey || !layerKey || !onAnimationFrame) {
      return noop$4;
    }

    const handleAnimation = timestamp => {
      const values = onAnimationFrame(timestamp);

      if (values) {
        const id = getLayerName(sourceKey, layerKey);
        Object.entries(values).forEach(([key, value]) => {
          map.setPaintProperty(id, key, value);
        });
      }

      animationKeyRef.current = requestAnimationFrame(handleAnimation);
    };

    animationKeyRef.current = requestAnimationFrame(handleAnimation);
    return () => {
      if (animationKeyRef.current) {
        cancelAnimationFrame(animationKeyRef.current);
      }
    };
  }, [map, sourceKey, layerKey, onAnimationFrame]);
  return null;
};

const noop$3 = () => {};

const defaultDrawOptions = {
  displayControlsDefault: false,
  controls: {
    point: true,
    polygon: true,
    trash: true
  }
};
const disabledClassName = 'disabled-map-draw-control';

const MapShapeEditor = props => {
  const {
    onCreate,
    onDelete,
    onUpdate,
    onModeChange,
    geoJsons,
    drawOptions = defaultDrawOptions,
    drawPosition = 'bottom-right',
    disabled
  } = props;
  const {
    map,
    mapStyle,
    isMapDestroyed,
    mapContainerRef
  } = useContext(MapChildContext);
  const [initialDrawOptions] = useState(drawOptions);
  const [initialDrawPosition] = useState(drawPosition);
  const drawRef = useRef(); // Create and destroy control

  useEffect(() => {
    if (!map || !mapStyle) {
      return noop$3;
    }

    const draw = new MapboxDraw(initialDrawOptions);
    map.addControl(draw, initialDrawPosition);
    drawRef.current = draw;
    return () => {
      if (!isMapDestroyed()) {
        map.removeControl(draw);
      }
    };
  }, [map, mapStyle, isMapDestroyed, initialDrawOptions, initialDrawPosition]); // Handle change in draw.create

  useEffect(() => {
    if (!map || !mapStyle || disabled) {
      return noop$3;
    }

    const handleCreate = e => {
      if (onCreate && drawRef.current) {
        onCreate(e.features, drawRef.current);
      } // TODO: add promoteId, set id in properties

    };

    map.on('draw.create', handleCreate);
    return () => {
      if (!isMapDestroyed()) {
        map.off('draw.create', handleCreate);
      }
    };
  }, [map, mapStyle, onCreate, isMapDestroyed, disabled]); // Handle change in draw.update

  useEffect(() => {
    if (!map || !mapStyle || disabled) {
      return noop$3;
    }

    const handleUpdate = e => {
      if (onUpdate && drawRef.current) {
        onUpdate(e.features, drawRef.current);
      }
    };

    map.on('draw.update', handleUpdate);
    return () => {
      if (!isMapDestroyed()) {
        map.off('draw.update', handleUpdate);
      }
    };
  }, [map, mapStyle, onUpdate, isMapDestroyed, disabled]); // Handle change in draw.delete

  useEffect(() => {
    if (!map || !mapStyle || disabled) {
      return noop$3;
    }

    const handleDelete = e => {
      if (onDelete && drawRef.current) {
        onDelete(e.features, drawRef.current);
      }
    };

    map.on('draw.delete', handleDelete);
    return () => {
      if (!isMapDestroyed()) {
        map.off('draw.delete', handleDelete);
      }
    };
  }, [map, mapStyle, onDelete, isMapDestroyed, disabled]); // Handle change in draw.modechange

  useEffect(() => {
    if (!map || !mapStyle || disabled) {
      return noop$3;
    }

    const handleModeChange = e => {
      if (onModeChange && drawRef.current) {
        onModeChange(e.mode, drawRef.current);
      }
    };

    map.on('draw.modechange', handleModeChange);
    return () => {
      if (!isMapDestroyed) {
        map.off('draw.modechange', handleModeChange);
      }
    };
  }, [map, mapStyle, onModeChange, isMapDestroyed, disabled]); // Handle geojson update

  useEffect(() => {
    if (!map || !mapStyle || !drawRef.current) {
      return;
    }

    if (disabled) {
      drawRef.current.set({
        type: 'FeatureCollection',
        features: []
      });
    } else {
      drawRef.current.set({
        type: 'FeatureCollection',
        features: geoJsons
      });
    }
  }, [map, mapStyle, geoJsons, disabled]); // Handle disabling controls

  useEffect(() => {
    if (!mapContainerRef || !mapContainerRef.current) {
      return;
    }

    if (disabled) {
      mapContainerRef.current.className = _cs(mapContainerRef.current.className, disabledClassName);
    } else {
      const classNames = mapContainerRef.current.className.split(' ');
      const filteredClassNames = classNames.filter(name => name !== disabledClassName);
      mapContainerRef.current.className = _cs(...filteredClassNames);
    }
  }, [mapContainerRef, disabled]);
  return null;
};

MapShapeEditor.defaultProps = {
  geoJsons: [],
  disabled: false
};

const noop$2 = () => {};

const MapOrder = props => {
  const {
    map
  } = useContext(MapChildContext);
  const {
    ordering
  } = props;
  const timeoutRef = useRef();
  useEffect(() => {
    if (!map || !ordering) {
      return noop$2;
    } // NOTE: To ensure layers are mounted, we use setTimeout


    timeoutRef.current = window.setTimeout(() => {
      // NOTE: just to be safe, only try ordering layers that are mounted
      const validLayerIdentifiers = ordering.filter(layerIdentifier => !!map.getLayer(layerIdentifier));

      for (let i = 0; i < validLayerIdentifiers.length; i += 1) {
        for (let j = i + 1; j < validLayerIdentifiers.length; j += 1) {
          // NOTE: Moving layer with index j before layer i
          map.moveLayer(validLayerIdentifiers[i], validLayerIdentifiers[j]);
        }
      }
    }, 0);
    return () => {
      window.clearTimeout(timeoutRef.current);
    };
  }, [map, ordering]);
  return null;
};

const noop$1 = () => {};

const MapImage = props => {
  const {
    map,
    mapStyle,
    isMapDestroyed
  } = useContext(MapChildContext);
  const {
    name,
    url,
    image,
    imageOptions,
    onLoad
  } = props;
  const [initialName] = useState(name);
  const [initialUrl] = useState(url);
  const [initialImage] = useState(image);
  const [initialImageOptions] = useState(imageOptions);
  useEffect(() => {
    if (!map || !mapStyle) {
      return noop$1;
    }

    if (map.hasImage(initialName)) {
      console.error(`An image with name '${initialName}' already exists`);
    } else if (initialUrl) {
      if (onLoad) {
        onLoad(false);
      }

      map.loadImage(initialUrl, (error, loadedImage) => {
        if (isMapDestroyed()) {
          return;
        }

        if (error) {
          console.error(error);
          return;
        }

        map.addImage(initialName, loadedImage, initialImageOptions);

        if (onLoad) {
          onLoad(true);
        }
      });
    } else if (initialImage) {
      map.addImage(initialName, initialImage, initialImageOptions);

      if (onLoad) {
        onLoad(true);
      }
    }

    return () => {
      if (!isMapDestroyed() && map.hasImage(initialName)) {
        map.removeImage(initialName);

        if (onLoad) {
          onLoad(false);
        }
      }
    };
  }, [map, mapStyle, isMapDestroyed, initialName, initialImage, initialUrl, initialImageOptions, onLoad]);
  return null;
};

const resizeHandlers = {};

const handleResize = entries => {
  entries.forEach(entry => {
    const element = entry.target;

    if (element instanceof HTMLElement) {
      const key = element.dataset.resizeHandlerKey;

      if (key && resizeHandlers[key]) {
        const {
          contentRect: rect
        } = entry;
        resizeHandlers[key](rect);
      }
    }
  });
};

const observer = new ResizeObserver(handleResize);

const addResizeHandler = (ref, callback) => {
  const key = randomString(16);

  if (!ref.current) {
    return;
  } // eslint-disable-next-line no-param-reassign


  ref.current.dataset.resizeHandlerKey = key;
  resizeHandlers[key] = callback;
  observer.observe(ref.current);
};

const removeResizeHandler = ref => {
  if (!ref.current) {
    return;
  }

  observer.unobserve(ref.current);
  const key = ref.current.dataset.resizeHandlerKey;

  if (!key || !resizeHandlers[key]) {
    return;
  }

  delete resizeHandlers[key];
};

const useDimension = (targetRef, debounceDuration = 200) => {
  const timeoutRef = useRef();
  const [rect, setRect] = useState(undefined);
  useEffect(() => {
    if (!targetRef) {
      return () => {// noop
      };
    }

    addResizeHandler(targetRef, r => {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => {
        const different = !rect // || r.x !== rect.x
        // || r.y !== rect.y
        || r.width !== rect.width || r.height !== rect.height || r.top !== rect.top || r.right !== rect.right || r.bottom !== rect.bottom || r.left !== rect.left;

        if (different) {
          setRect(r);
        }
      }, debounceDuration);
    });
    return () => {
      window.clearTimeout(timeoutRef.current);
      removeResizeHandler(targetRef);
    };
  });
  return rect;
};

const style = {
  padding: 0
};

const MapContainer = props => {
  const {
    className
  } = props;
  const {
    mapContainerRef,
    map
  } = useContext(MapChildContext);
  const rect = useDimension(mapContainerRef);
  useEffect(() => {
    if (map) {
      map.resize();
    }
  }, [rect, map]);
  return /*#__PURE__*/React.createElement("div", {
    ref: mapContainerRef,
    className: className,
    style: style
  });
};

const MapBounds = props => {
  const {
    map,
    setBounds
  } = useContext(MapChildContext);
  const {
    padding,
    duration: durationFromProps,
    bounds
  } = props;
  const durationRef = useRef(durationFromProps);
  useEffect(() => {
    durationRef.current = durationFromProps;
  }, [durationFromProps]); // Handle change in bounds

  useEffect(() => {
    if (!map || !bounds) {
      return;
    }

    const duration = durationRef.current;
    setBounds(bounds, padding, duration);
    map.fitBounds(bounds, {
      padding,
      duration
    });
  }, [map, bounds, padding, setBounds]);
  return null;
};

MapBounds.defaultProps = {
  padding: 0,
  duration: 200 // ms

};

const MapCenter = props => {
  const {
    map
  } = useContext(MapChildContext);
  const {
    center,
    centerOptions
  } = props;
  const centerOptionsRef = useRef(centerOptions);
  useEffect(() => {
    centerOptionsRef.current = centerOptions;
  }, [centerOptions]); // Handle change in center

  useEffect(() => {
    if (!map || !center) {
      return;
    }

    map.flyTo({
      center,
      ...centerOptionsRef.current
    });
  }, [map, center]);
  return null;
};

MapCenter.defaultProps = {
  padding: 0,
  duration: 200 // ms

};

const UNSUPPORTED_BROWSER = !mapboxgl.supported();

function findLayerFromLayers(layers, layerKey) {
  const layer = layers.find(l => l.layerKey === layerKey);
  return layer;
}

function getLayersForSources(sources) {
  const layers = Object.entries(sources).filter(([, source]) => !!source.layers).map(([sourceKey, source]) => Object.entries(source.layers).map(([layerKey, layer]) => ({ ...layer,
    sourceKey,
    layerKey: getLayerName(sourceKey, layerKey)
  })));
  return layers.flat();
} // eslint-disable-next-line @typescript-eslint/no-empty-function


function noop() {}

function Map(props) {
  const {
    mapStyle: mapStyleFromProps,
    mapOptions,
    scaleControlPosition,
    scaleControlShown,
    scaleControlOptions,
    navControlShown,
    navControlOptions,
    navControlPosition,
    children,
    debug
  } = props;
  const [initialDebug] = useState(debug);
  const [initialMapStyle] = useState(mapStyleFromProps);
  const [initialMapOptions] = useState(mapOptions);
  const [initialNavControlOptions] = useState(navControlOptions);
  const [initialNavControlPosition] = useState(navControlPosition);
  const [initialNavControlShown] = useState(navControlShown);
  const [initialScaleControlOptions] = useState(scaleControlOptions);
  const [initialScaleControlPosition] = useState(scaleControlPosition);
  const [initialScaleControlShown] = useState(scaleControlShown);
  const [mapStyle, setMapStyle] = useState(undefined);
  const [loaded, setLoaded] = useState(false);
  const boundsRef = useRef();
  const paddingRef = useRef();
  const durationRef = useRef();
  const dragging = useRef(undefined);
  const lastIn = useRef(undefined);
  const mapDestroyedRef = useRef(false);
  const sourcesRef = useRef({});
  const mapRef = useRef(undefined);
  const mapContainerRef = useRef(null);
  const setBounds = useCallback((bounds, padding, duration) => {
    boundsRef.current = bounds;
    paddingRef.current = padding;
    durationRef.current = duration;
  }, []); // Create map

  useEffect(() => {
    if (UNSUPPORTED_BROWSER) {
      console.error('No Mapboxgl support.');
      return noop;
    }

    const {
      current: mapContainer
    } = mapContainerRef;

    if (!mapContainer) {
      console.error('No container found.');
      return noop;
    }

    const mapboxglMap = new mapboxgl.Map({
      container: mapContainer,
      style: initialMapStyle,
      preserveDrawingBuffer: true,
      ...initialMapOptions
    });
    mapRef.current = mapboxglMap; // FIXME: we shouldn't always set cursor to pointer
    // mapboxglMap.getCanvas().style.cursor = 'pointer';

    if (initialScaleControlShown) {
      const scale = new mapboxgl.ScaleControl(initialScaleControlOptions);
      mapboxglMap.addControl(scale, initialScaleControlPosition);
    }

    if (initialNavControlShown) {
      // NOTE: don't we need to remove control on unmount?
      const nav = new mapboxgl.NavigationControl(initialNavControlOptions);
      mapboxglMap.addControl(nav, initialNavControlPosition);
    }
    /*
    // TODO: need to resize map in some cases
    const timer = setTimeout(() => {
        mapboxglMap.resize();
    }, 200);
    */


    const handleMouseDown = data => {
      const {
        current: map
      } = mapRef;

      if (!map) {
        return;
      }

      if (dragging.current) {
        dragging.current = undefined;
        mapboxglMap.getCanvas().style.cursor = '';
      }

      const {
        point
      } = data;
      const layers = getLayersForSources(sourcesRef.current);
      const draggableLayerKeys = layers.filter(layer => !!layer.onDrag).map(layer => layer.layerKey);
      const draggableFeatures = map.queryRenderedFeatures(point, {
        layers: draggableLayerKeys
      });

      if (draggableFeatures.length <= 0) {
        if (initialDebug) {
          console.warn('No draggable layer found.');
        }

        return;
      }

      const firstFeature = draggableFeatures[0];
      dragging.current = {
        id: firstFeature.id,
        layerName: firstFeature.layer.id,
        sourceName: firstFeature.source // sourceLayer: firstFeature.sourceLayer,

      };
      mapboxglMap.getCanvas().style.cursor = 'grabbing';
      data.preventDefault();
    };

    const handleMouseUp = data => {
      const {
        current: map
      } = mapRef;

      if (!map) {
        return;
      }

      if (!dragging.current) {
        return;
      }

      const {
        point,
        lngLat
      } = data;
      const {
        layerName
      } = dragging.current;
      const layers = getLayersForSources(sourcesRef.current);
      const layer = findLayerFromLayers(layers, layerName);

      if (layer && layer.onDragEnd) {
        layer.onDragEnd(dragging.current, lngLat, point, map);
      } // set dragging false


      dragging.current = undefined;
      mapboxglMap.getCanvas().style.cursor = '';
    };

    const handleClick = data => {
      const {
        current: map
      } = mapRef;

      if (!map) {
        return;
      }

      if (dragging.current) {
        dragging.current = undefined;
        mapboxglMap.getCanvas().style.cursor = '';
        return;
      }

      const layers = getLayersForSources(sourcesRef.current);
      const {
        point,
        lngLat
      } = data;
      const clickableLayerKeys = layers.filter(layer => !!layer.onClick).map(layer => layer.layerKey);
      const clickableFeatures = map.queryRenderedFeatures(point, {
        layers: clickableLayerKeys
      });

      if (clickableFeatures.length <= 0) {
        if (initialDebug) {
          console.warn('No clickable layer found.');
        } // TODO: add a global handler


        return;
      }

      clickableFeatures.every(clickableFeature => {
        const {
          layer: {
            id
          }
        } = clickableFeature;
        const layer = findLayerFromLayers(layers, id);

        if (layer && layer.onClick) {
          return !layer.onClick(clickableFeature, lngLat, point, map);
        }

        return false;
      });
    };

    const handleDoubleClick = data => {
      const {
        current: map
      } = mapRef;

      if (!map) {
        return;
      }

      if (dragging.current) {
        dragging.current = undefined;
        mapboxglMap.getCanvas().style.cursor = '';
        return;
      }

      const layers = getLayersForSources(sourcesRef.current);
      const {
        point,
        lngLat
      } = data;
      const clickableLayerKeys = layers.filter(layer => !!layer.onDoubleClick).map(layer => layer.layerKey);
      const clickableFeatures = map.queryRenderedFeatures(point, {
        layers: clickableLayerKeys
      });

      if (clickableFeatures.length <= 0) {
        if (initialDebug) {
          console.warn('No clickable layer found.');
        } // TODO: add a global handler


        return;
      }

      clickableFeatures.every(clickableFeature => {
        const {
          layer: {
            id
          }
        } = clickableFeature;
        const layer = findLayerFromLayers(layers, id);

        if (layer && layer.onDoubleClick) {
          return !layer.onDoubleClick(clickableFeature, lngLat, point, map);
        }

        return false;
      });
    };

    const handleMouseMove = data => {
      const {
        current: map
      } = mapRef;

      if (!map) {
        return;
      }

      const {
        point,
        lngLat
      } = data;

      if (dragging.current) {
        const {
          layerName
        } = dragging.current;
        const layers = getLayersForSources(sourcesRef.current);
        const layer = findLayerFromLayers(layers, layerName);

        if (layer && layer.onDrag) {
          layer.onDrag(dragging.current, lngLat, point, map);
        } // TODO: do some other things


        return;
      }

      const layers = getLayersForSources(sourcesRef.current);
      const interactiveLayerKeys = layers.filter(layer => !!layer.onClick || !!layer.onDoubleClick || !!layer.onDrag).map(layer => layer.layerKey);
      const interactiveFeatures = map.queryRenderedFeatures(point, {
        layers: interactiveLayerKeys
      });

      if (interactiveFeatures.length <= 0) {
        mapboxglMap.getCanvas().style.cursor = '';
      } else {
        mapboxglMap.getCanvas().style.cursor = 'pointer';
      }

      const hoverableLayerKeys = layers.filter(layer => !!layer.onMouseEnter || !!layer.onMouseLeave).map(layer => layer.layerKey);
      const hoverableFeatures = map.queryRenderedFeatures(point, {
        layers: hoverableLayerKeys
      });

      if (hoverableFeatures.length <= 0) {
        if (lastIn.current) {
          mapboxglMap.removeFeatureState({
            id: lastIn.current.id,
            source: lastIn.current.sourceName,
            sourceLayer: lastIn.current.sourceLayer
          }, 'hovered');
          const layer = findLayerFromLayers(layers, lastIn.current.layerName);

          if (layer && layer.onMouseLeave) {
            layer.onMouseLeave(map);
          }
        }

        lastIn.current = undefined;
        return;
      }

      const firstFeature = hoverableFeatures[0];

      if (!lastIn.current || firstFeature.source !== lastIn.current.sourceName || firstFeature.sourceLayer !== lastIn.current.sourceLayer || firstFeature.layer.id !== lastIn.current.layerName || firstFeature.id !== lastIn.current.id) {
        if (lastIn.current) {
          mapboxglMap.removeFeatureState({
            id: lastIn.current.id,
            source: lastIn.current.sourceName,
            sourceLayer: lastIn.current.sourceLayer
          }, 'hovered');
        }

        if (lastIn.current && (firstFeature.source !== lastIn.current.sourceName || firstFeature.sourceLayer !== lastIn.current.sourceLayer || firstFeature.layer.id !== lastIn.current.layerName)) {
          const layer = findLayerFromLayers(layers, lastIn.current.layerName);

          if (layer && layer.onMouseLeave) {
            layer.onMouseLeave(map);
          }
        }

        lastIn.current = {
          id: firstFeature.id,
          layerName: firstFeature.layer.id,
          sourceName: firstFeature.source,
          sourceLayer: firstFeature.sourceLayer
        };
        mapboxglMap.setFeatureState({
          id: lastIn.current.id,
          source: lastIn.current.sourceName,
          sourceLayer: lastIn.current.sourceLayer
        }, {
          hovered: true
        });
        const {
          layer: {
            id
          }
        } = firstFeature;
        const layer = findLayerFromLayers(layers, id);

        if (layer && layer.onMouseEnter) {
          layer.onMouseEnter(firstFeature, lngLat, point, map);
        }
      }
    };

    const handleResize = () => {
      const {
        current: map
      } = mapRef;

      if (!map) {
        return;
      }

      if (!map) {
        return;
      }

      if (!boundsRef.current) {
        return;
      }

      map.fitBounds(boundsRef.current, {
        padding: paddingRef.current,
        duration: durationRef.current
      });
    };

    mapboxglMap.on('click', handleClick);
    mapboxglMap.on('dblclick', handleDoubleClick);
    mapboxglMap.on('mousemove', handleMouseMove);
    mapboxglMap.on('resize', handleResize);
    mapboxglMap.on('mousedown', handleMouseDown);
    mapboxglMap.on('mouseup', handleMouseUp);

    const destroy = () => {
      // clearTimeout(timer);
      mapboxglMap.off('click', handleClick);
      mapboxglMap.off('dblclick', handleDoubleClick);
      mapboxglMap.off('mousemove', handleMouseMove);
      mapboxglMap.off('resize', handleResize); // FIXME: looks like mousedown and mouseup aren't handled

      sourcesRef.current = {};
      lastIn.current = undefined;
      mapDestroyedRef.current = true;

      if (initialDebug) {
        console.warn('Removing map');
      }

      mapboxglMap.remove();
    };

    return destroy;
  }, [initialDebug, initialMapStyle, initialMapOptions, initialNavControlOptions, initialNavControlPosition, initialNavControlShown, initialScaleControlOptions, initialScaleControlPosition, initialScaleControlShown]); // Handle style load and map ready

  useEffect(() => {
    const {
      current: map
    } = mapRef;

    if (UNSUPPORTED_BROWSER || !map || !mapStyleFromProps) {
      return noop;
    }

    sourcesRef.current = {};
    lastIn.current = undefined;

    if (initialDebug) {
      console.warn(`Setting map style ${mapStyleFromProps}`);
    }

    map.setStyle(mapStyleFromProps);

    const onStyleData = () => {
      if (initialDebug) {
        console.info('Passing mapStyle:', mapStyleFromProps);
      }

      setMapStyle(mapStyleFromProps);
    };

    map.once('styledata', onStyleData); // FIXME: This will only be called once, should be moved when
    // map object is created

    const onLoad = () => {
      setLoaded(true);
    };

    map.once('load', onLoad);
    return () => {
      if (mapRef.current) {
        mapRef.current.off('styledata', onStyleData);
        mapRef.current.off('load', onLoad);
      }
    };
  }, [mapStyleFromProps, initialDebug]);
  const isMapDestroyed = useCallback(() => !!mapDestroyedRef.current, []);
  const isSourceDefined = useCallback(sourceKey => !!sourcesRef.current[sourceKey], []);
  const getSource = useCallback(sourceKey => sourcesRef.current[sourceKey], []);
  const setSource = useCallback(source => {
    sourcesRef.current = produce(sourcesRef.current, safeSource => {
      const {
        name
      } = source; // eslint-disable-next-line no-param-reassign

      safeSource[name] = source;
    });
  }, []);
  const removeSource = useCallback(sourceKey => {
    if (!sourcesRef.current[sourceKey]) {
      return;
    }

    sourcesRef.current = produce(sourcesRef.current, safeSource => {
      // eslint-disable-next-line no-param-reassign
      delete safeSource[sourceKey];
    });
    const {
      current: map
    } = mapRef;

    if (map) {
      if (initialDebug) {
        console.warn(`Removing source: ${sourceKey}`);
      }

      map.removeSource(sourceKey);
    }
  }, [initialDebug]);
  const mapChildren = children;

  if (UNSUPPORTED_BROWSER) {
    return mapChildren;
  }

  const childrenProps = {
    map: mapRef.current,
    mapStyle: loaded ? mapStyle : undefined,
    mapContainerRef,
    isSourceDefined,
    getSource,
    setSource,
    removeSource,
    isMapDestroyed,
    setBounds,
    debug: initialDebug
  };
  return /*#__PURE__*/React.createElement(MapChildContext.Provider, {
    value: childrenProps
  }, mapChildren);
}

Map.defaultProps = {
  scaleControlShown: false,
  scaleControlPosition: 'bottom-right',
  navControlShown: false,
  navControlPosition: 'top-right',
  debug: false
};

export default Map;
export { MapBounds, MapCenter, MapChildContext, MapContainer, MapImage, MapLayer, MapOrder, MapShapeEditor, MapSource, MapState, MapTooltip, SourceChildContext, getLayerName, setMapboxToken, usePrevious };
//# sourceMappingURL=index.es.js.map
